import { Link } from 'react-router-dom';
import { Menu, RadioTower } from 'lucide-react';
import { useTheme } from '@/hooks/useTheme';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { AvatarMenu } from './AvatarMenu';
import Logo from './Logo';
import { TLButton } from '../ui/TLButton';
import { cn, darkenHexColor } from '@/lib/utils';
import { useTranslation } from '@/hooks/useTranslation';

type HeaderProps = {
  onMenuToggle?: () => void;
  className?: string;
};

export function Header({ onMenuToggle, className = '' }: HeaderProps) {
  const { colors } = useTheme();

  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();

  const gradientFrom = darkenHexColor(colors.primary, -12);
  const gradientMid = colors.primary;
  const gradientTo = darkenHexColor(colors.primary, 10);
  const actionBackground = darkenHexColor(colors.primary, 12);

  return (
    <header
      className={cn(
        'sticky top-0 z-40 border-b border-white/20 px-4 py-2 shadow-lg backdrop-blur-xl dark:border-white/5',
        className
      )}
      style={{
        background: `linear-gradient(120deg, ${gradientFrom} 0%, ${gradientMid} 55%, ${gradientTo} 100%)`,
        color: colors.primaryText,
      }}
    >
      <div className="mx-auto flex w-full max-w-6xl flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          {onMenuToggle && (
            <TLButton
              type="button"
              className="transition"
              size="icon"
              onClick={onMenuToggle}
              textColor={colors.primaryText}
              backgroundColor={actionBackground}
              aria-label={t('header.menuToggle')}
            >
              <Menu className="h-5 w-5" />
            </TLButton>
          )}
          <Link
            to="/"
            className="flex items-center gap-3 rounded-2xl border border-white/20 bg-white/10 px-3 py-1.5 text-left shadow-sm transition hover:-translate-y-0.5 hover:bg-white/20"
          >
            <Logo className="h-7 w-auto" />
            <span className="flex flex-col">
              <span className="inline-flex items-center gap-1 text-2xs font-semibold uppercase tracking-[0.45em] opacity-75">
                <RadioTower className="h-3.5 w-3.5" /> {t('common.brand')}
              </span>
            </span>
          </Link>
        </div>

        <div className="flex items-center gap-2">{isAuthenticated && <AvatarMenu />}</div>
      </div>
    </header>
  );
}
